import { ErrorResponse } from '@gtpl/shared-models/common-models';
import { EmployeeRequest, EmployeesDeptWiseCountDto, EmployeesDeptWiseResponseModel } from '@gtpl/shared-models/gtpl';
import { Injectable } from '@nestjs/common';
import { InjectRepository } from '@nestjs/typeorm';
import { getConnection, getManager, Raw, Repository } from 'typeorm';
import { EmployeeDetailsAdapter } from './dto/employee-details.adapter';
import { EmployeeDetailsDTO } from './dto/employee-details.dto';
import { EmployeeDetails } from './employee-details.entity';
import { EmployeeService } from '@gtpl/shared-services/attendance';
import { EmployeeRoleRequest } from './dto/employee-role.request';
import { HrmsResponseModel, HrmsAllResponseModel, CommonResponse, EmployeeResponseModel, AllEmployeesResponseModel, EmployeeDepartmentRequest } from '@gtpl/shared-models/masters';
import { EmployeeDesignationRequest } from './dto/employee-designation';
import { EmployeeDeptRequest } from './dto/employee-dept.request';
import { Departments } from '../departments/departments.entity';
import { EmployeeCodeRequest } from './dto/employee-code.request';
import { EmployeeRepo } from './dto/employee.repository';
import { DeptRequest } from './dto/dept-request';
import appSettings from 'apps/services/config';
import { EmployeeByUnitRequest } from './dto/employee-by-unit-request';
import { AttendanceService } from '@gtpl/shared-services/hrms';
import { GenericTransactionManager } from 'apps/services/backend/typeorm-transactions/generic-transaction-manager';
import { EmployeeCodesRequest } from '@gtpl/shared-models/hrms';

@Injectable()
export class EmployeeDetailsService {

  constructor(
    private employeeDetailsRepository: EmployeeRepo,
    @InjectRepository(Departments)
    private deptRepository: Repository<Departments>,
    private employeeDetailsAdapter: EmployeeDetailsAdapter,
    private userService: EmployeeService,
    private attendanceService: AttendanceService
  ) { }

  async getEmployeeWithoutRelations(employeeCode: string): Promise<EmployeeDetails> {
    const EmployeeResponse = await this.employeeDetailsRepository.findOne({
      where: { employeeCode: Raw(alias => `employee_code = '${employeeCode}'`) },
    });
    if (EmployeeResponse) {
      return EmployeeResponse;
    }
    else {
      return null;
    }
  }

  async getEmployeeMobile(mobileNumber: string): Promise<EmployeeDetails> {
    const EmployeeResponse = await this.employeeDetailsRepository.findOne({
      where: { mobileNumber: Raw(alias => `mobile_no = '${mobileNumber}'`) },
    });
    if (EmployeeResponse) {
      return EmployeeResponse;
    }
    else {
      return null;
    }
  }

  async getEmployeeAadhaar(aadhaarNumber: string): Promise<EmployeeDetails> {
    const EmployeeResponse = await this.employeeDetailsRepository.findOne({
      where: { aadhaarNumber: Raw(alias => `aadhaar_number = '${aadhaarNumber}'`) },
    });
    if (EmployeeResponse) {
      return EmployeeResponse;
    }
    else {
      return null;
    }
  }

  async createEmployee(employeeDetailsDTO: EmployeeDetailsDTO, isUpdate: boolean): Promise<HrmsResponseModel> {
    const transManager = new GenericTransactionManager();
    try {
      if (!isUpdate) {
        const EmployeeEntity = await this.getEmployeeWithoutRelations(employeeDetailsDTO.employeeCode);
        if (EmployeeEntity) {
          return new HrmsResponseModel(false, 11104, 'Employee already exists');
        }
        const EmployeeEntity1 = await this.getEmployeeMobile(employeeDetailsDTO.mobileNumber);
        if (EmployeeEntity1) {
          return new HrmsResponseModel(false, 11164, 'Mobile Number already exists');
        }
        const EmployeeEntity2 = await this.getEmployeeAadhaar(employeeDetailsDTO.aadhaarNumber);
        if (EmployeeEntity2) {
          return new HrmsResponseModel(false, 11544, 'Aaadhaar Number already exists');
        }

        if (employeeDetailsDTO.employeeType === 'Salaried Form') {
          const empDetails = await this.employeeDetailsRepository.getExcutiveEmployeeCode();
          if (empDetails) {
            const lastEmpCode = empDetails.employeeCode;
            employeeDetailsDTO.employeeCode = "SA" + (Number(lastEmpCode.toString().slice(2)) + 1);
          } else {
            employeeDetailsDTO.employeeCode = "SA25";
          }
        } else {
          const empDetails = await this.employeeDetailsRepository.getWorkerEmployeeCode();
          if (empDetails) {
            const lastEmpCode = empDetails.employeeCode;
            employeeDetailsDTO.employeeCode = "SAW" + (Number(lastEmpCode.toString().slice(3)) + 1);
          } else {
            employeeDetailsDTO.employeeCode = "SAW1";
          }
        }
      }
      const req = new EmployeeCodesRequest([employeeDetailsDTO.employeeCode]);
      await transManager.startTransaction();
      const convertedEmployeeEntity: EmployeeDetails = this.employeeDetailsAdapter.convertDtoToEntity(employeeDetailsDTO, isUpdate);
      const savedEmployeeEntity = await this.employeeDetailsRepository.save(convertedEmployeeEntity);
      if (savedEmployeeEntity) {
        const createLogs = await this.attendanceService.createEmpAttnLogForToday(req);
        if (createLogs) {
          const savedEmployeeDto: EmployeeDetailsDTO = this.employeeDetailsAdapter.convertEntityToDto(savedEmployeeEntity);
          if (isUpdate == false) {
            const userService = await this.userService.createEmployee(savedEmployeeDto);
          }
          if (isUpdate == true) {
            const userService = await this.userService.updateEmployee(savedEmployeeDto);
          }
          if (savedEmployeeDto) {
            await transManager.completeTransaction();
            return new HrmsResponseModel(true, 1, isUpdate == true ? 'Employee Details updated successfully' : 'Employee Details saved successfully', savedEmployeeDto);
          } else {
            await transManager.releaseTransaction();
            return new HrmsResponseModel(false, 11110, 'Something went wrong while converting to DTO')
          }
        } else {
          await transManager.releaseTransaction();
          return new HrmsResponseModel(false, 11110, 'Something went wrong while creating attendance logs')
        }
      } else {
        await transManager.releaseTransaction();
        return new HrmsResponseModel(false, 11110, 'Something went wrong while saving employee')
      }
    } catch (error) {
      await transManager.releaseTransaction();
      return error;
    }
  }

  // async getAllEmployee(): Promise<HrmsAllResponseModel> {
  //   try {
  //     const employeeDetailsDTO: EmployeeDetailsDTO[] = [];
  //     const EmployeeEntity: EmployeeDetails[] = await this.employeeDetailsRepository.find({ order: { createdAt: 'DESC' } });
  //     const deptIds: number[] = Array.from(new Set(EmployeeEntity.map(res => Number(res.departmentId))));
  //     const AllDepartmentsResponseModel = await this.deptService.getDepartmentNameByDeptIds(deptIds);
  //     if (EmployeeEntity) {
  //       const deptMap = new Map();
  //       AllDepartmentsResponseModel?.data?.forEach(res => {
  //         deptMap.set(res.deptId, res.deptName);
  //       });
  //       EmployeeEntity.forEach(EmployeeEntity => {
  //         const convertedEmployeeDto: EmployeeDetailsDTO = this.employeeDetailsAdapter.convertEntityToDto(EmployeeEntity);
  //         convertedEmployeeDto.departmentId = deptMap.get(Number(EmployeeEntity.departmentId)) ? deptMap.get(Number(EmployeeEntity.departmentId)) : EmployeeEntity.departmentId;
  //         employeeDetailsDTO.push(convertedEmployeeDto);
  //       });
  //       const response = new HrmsAllResponseModel(true, 1, 'Employees retrieved successfully', employeeDetailsDTO);
  //       return response;
  //     } else {
  //       return new HrmsAllResponseModel(false,99998, 'Data not found');
  //     }
  //   } catch (err) {
  //     throw err;
  //   }
  // }

  async getAllEmployee(): Promise<CommonResponse> {
    const manager = getManager();
    const query = ` SELECT ed.employee_id AS employeeId, unit_id AS unitId , employee_role AS employeeRole, employee_code AS employeeCode, 
    employee_name AS employeeName, emp_last_name AS employeeLastName, mobile_no AS mobileNumber, prefix AS prefix, gender AS gender, 
    maritual_status AS maritualStatus, alt_mobile_no AS altMobileNo, priority_one AS priorityOne, 
    priority_type AS priorityType, blood_group AS bloodGroup,  date_of_birth AS dateOfBirth, nationality AS nationality, 
    current_address AS currentAddress, years_of_exp AS yearsOfExperience, company_email AS companyEmail, qualification AS qualification, 
    employee_mode AS employeeMode, employee_type AS employeeType ,email_id AS emailId, permanent_address AS permanentAddress,
    employee_status AS employeeStatus, contractor_name AS contractorName, shift_group AS shiftGroup, date_of_join AS dateOfJoin, 
    date_of_leave AS dateOfLeave, date_of_entry AS dateOfEntry, workstation_number AS workstationNumber, 
    health_card_number AS healthCardNumber, uan_number AS uanNumber, salary AS salary, bank_account_number AS bankAccountNumber, 
    bank_name AS bankName, ifsc_code AS ifscCode, ed.designation AS designation, emergency_contact_number AS emergencyContactNumber, 
    emergency_contact_name AS emergencyContactName, is_probationary_completed AS isProbationaryCompleted,transportation AS transportation, 
    accommodation AS accommodation, is_audit_completed AS isAuditCompleted, skill_set AS skillSet,pickup_point AS pickupPoint, 
    route_number AS routeNumber,esi_number AS esiNumber, reporting_manager AS reportingManager, emp_category AS empCategory, 
    wage_type AS wageType, aadhaar_number AS aadhaarNumber, state AS state, pin_code AS pinCode, permanent_state AS permanentState, 
    permanent_pin_code AS permanentPinCode, ed.is_active AS isActive, ed.created_at AS createdAt, ed.created_user AS createdUser, 
    ed.updated_at AS updatedAt, ed.updated_user AS updatedUser, ed.version_flag AS versionFlag, efd.employee_family_id, efd.family_mem_name,
   efd.relation, efd.fam_date_of_birth, efd.fam_aadhaar_no, efd.contact_no, eed.employee_education_id, eed.emp_qualification, 
   eed.specialization, eed.percentage, eed.date_of_passout, exd.designation AS expDesignation, exd.employee_experience_id , exd.organisation, 
   exd.from_date , exd.to_date,exd.experience, eid.employee_idproof_id, eid.id_type, eid.id_number, d.dept_name AS department, 
   ed.department_id AS departmentId FROM ` + appSettings.masters_db + `.employee_details ed
   LEFT JOIN ` + appSettings.masters_db + `.employee_family_details efd ON efd.employee_id = ed.employee_id 
   LEFT JOIN ` + appSettings.masters_db + `.employee_education_details eed ON eed.employee_id = ed.employee_id 
   LEFT JOIN ` + appSettings.masters_db + `.employee_experience_details exd ON exd.employee_id = ed.employee_id 
   LEFT JOIN ` + appSettings.masters_db + `.employee_idproof_details eid ON eid.employee_id = ed.employee_id 
   LEFT JOIN ` + appSettings.masters_db + `.departments d ON d.dept_id = ed.department_id 
   WHERE ed.employee_id > 0 GROUP BY ed.employee_id `
    const data = await manager.query(query)
    if (data.length > 0) {
      return new CommonResponse(true, 2255, 'Data Retreived sucessfilly', data)
    } else {
      return new CommonResponse(false, 1010, 'No data Found')
    }
  }

  async getAllActiveEmployees(): Promise<HrmsAllResponseModel> {
    try {
      const employeeDetailsDTO: EmployeeDetailsDTO[] = [];
      //retrieves all companies
      const EmployeesEntities: EmployeeDetails[] = await this.employeeDetailsRepository.find({
        order: { employeeName: 'ASC' }, where: { isActive: true },
      });
      if (EmployeesEntities) {
        // converts the data fetched from the database which of type companies array to type StateDto array.
        EmployeesEntities.forEach(EmployeeEntity => {
          const convertedEmployeeDto: EmployeeDetailsDTO = this.employeeDetailsAdapter.convertEntityToDto(
            EmployeeEntity
          );
          employeeDetailsDTO.push(convertedEmployeeDto);
        });
        const response = new HrmsAllResponseModel(true, 11108, "Employees retrieved successfully", employeeDetailsDTO);
        return response;
      } else {
        throw new ErrorResponse(99998, 'Data not found'); employeeDetailsDTO
      }
    } catch (err) {
      throw err;
    }
  }

  async getActiveEmployeesById(employeeReq: EmployeeRequest): Promise<HrmsResponseModel> {
    try {
      //retrieves all companies
      const employeeEntities: EmployeeDetails = await this.employeeDetailsRepository.findOne({
        where: { employeeId: employeeReq.employeeId }
      });

      const employeeData: EmployeeDetailsDTO = this.employeeDetailsAdapter.convertEntityToDto(employeeEntities);
      if (employeeData) {
        const response = new HrmsResponseModel(true, 11101, 'employee retrived Successfully', employeeData);
        return response;
      }
      else {
        throw new ErrorResponse(11106, 'Something went wrong');
      }
      // generating resposnse
    } catch (err) {
      throw err;
    }
  }

  async getActiveEmpDeptWiseCount(deptReq: DeptRequest): Promise<EmployeesDeptWiseResponseModel> {
    try {
      const deptId = deptReq.deptId;
      const employeeDtos: EmployeesDeptWiseCountDto[] = [];

      const empCount = await getConnection()
        .createQueryBuilder()
        .select("department_id as departmentId, count(*) as count")
        .from(EmployeeDetails, 'employee_details')
        .where(`department_id = ${deptId}`)
        .getRawOne();

      if (empCount) {
        return new EmployeesDeptWiseResponseModel(true, 11111, "", empCount);
      } else {
        throw new ErrorResponse(99998, 'enPmsLabels.errorCodes[99998]');
      }
    } catch (err) {
      throw err;
    }
  }

  async getActiveEmployeesByEmpCode(empCode: EmployeeCodeRequest): Promise<EmployeeResponseModel> {
    try {
      const employeeEntities: EmployeeDetails = await this.employeeDetailsRepository.findOne({
        where: { employeeCode: empCode.employeeCode, isActive: true }
      });

      const employeeData: EmployeeDetailsDTO = this.employeeDetailsAdapter.convertEntityToDto(employeeEntities);
      if (employeeData) {
        const response = new EmployeeResponseModel(true, 11101, 'employee retrived Successfully', employeeData);
        return response;
      }
      else {
        throw new ErrorResponse(11106, 'Something went wrong');
      }
      // generating resposnse
    } catch (err) {
      throw err;
    }
  }

  async getEmployeeByEmpCode(empCode: EmployeeCodeRequest): Promise<EmployeeResponseModel> {
    try {
      const employeeEntities: EmployeeDetails = await this.employeeDetailsRepository.findOne({
        where: { employeeCode: empCode.employeeCode }
      });

      const employeeData: EmployeeDetailsDTO = this.employeeDetailsAdapter.convertEntityToDto(employeeEntities);
      if (employeeData) {
        const response = new EmployeeResponseModel(true, 11101, 'employee retrived Successfully', employeeData);
        return response;
      }
      else {
        throw new ErrorResponse(11106, 'Something went wrong');
      }
      // generating resposnse
    } catch (err) {
      throw err;
    }
  }



  async getActiveEmployeesByRole(employeeRoleRequest: EmployeeRoleRequest): Promise<HrmsAllResponseModel> {
    try {
      //retrieves all companies
      const employeeEntities: EmployeeDetails[] = await this.employeeDetailsRepository.find({
        where: { employeeRole: employeeRoleRequest.employeeRole, isActive: true }
      });
      if (employeeEntities.length === 0) {
        throw new ErrorResponse(10000, 'No Records found');
      }
      const employeeData: EmployeeDetailsDTO[] = [];
      employeeEntities.map(employeData => {
        employeeData.push(this.employeeDetailsAdapter.convertEntityToDto(employeData));
      })
      return new HrmsAllResponseModel(true, 11101, 'employee retrived Successfully', employeeData);
    } catch (err) {
      throw err;
    }
  }

  async getActiveEmployeesByDept(employeeDeptRequest: EmployeeDeptRequest): Promise<HrmsAllResponseModel> {
    try {
      const getDeptDetails = await this.deptRepository.findOne({ where: { deptName: employeeDeptRequest.employeeDept } });
      const employeeEntities: EmployeeDetails[] = await this.employeeDetailsRepository.find({
        where: { departmentId: getDeptDetails.deptId, isActive: true }
      });
      if (employeeEntities.length === 0) {
        throw new ErrorResponse(10000, 'No Records found');
      }
      const employeeData: EmployeeDetailsDTO[] = [];
      employeeEntities.map(employeData => {
        employeeData.push(this.employeeDetailsAdapter.convertEntityToDto(employeData));
      })
      return new HrmsAllResponseModel(true, 11101, 'employee retrived Successfully', employeeData);
    }
    catch (err) {
      throw err;
    }
  }

  async getActiveEmployeesByDeptId(deptId: number): Promise<HrmsAllResponseModel> {
    try {
      const getDeptDetails = await this.deptRepository.findOne({ where: { deptId: deptId } });
      const employeeEntities: EmployeeDetails[] = await this.employeeDetailsRepository.find({
        where: { departmentId: getDeptDetails.deptId, isActive: true }
      });
      if (employeeEntities.length === 0) {
        throw new ErrorResponse(10000, 'No Records found');
      }
      const employeeData: EmployeeDetailsDTO[] = [];
      employeeEntities.map(employeData => {
        employeeData.push(this.employeeDetailsAdapter.convertEntityToDto(employeData));
      })
      return new HrmsAllResponseModel(true, 11101, 'employee retrived Successfully', employeeData);
    }
    catch (err) {
      throw err;
    }
  }

  async getAllActiveEmployeesForLeaves(): Promise<HrmsAllResponseModel> {
    // const page: number = 1;
    try {
      const employeeDetailsDTO: EmployeeDetailsDTO[] = [];
      //retrieves all companies
      const EmployeesEntities: EmployeeDetails[] = await this.employeeDetailsRepository.find({
        order: { employeeCode: 'ASC' }, where: { isActive: true },
      });
      if (EmployeesEntities) {
        // converts the data fetched from the database which of type companies array to type StateDto array.
        EmployeesEntities.forEach(EmployeeEntity => {
          const convertedEmployeeDto: EmployeeDetailsDTO = this.employeeDetailsAdapter.convertEntityToDto(
            EmployeeEntity
          );
          employeeDetailsDTO.push(convertedEmployeeDto);
        });
        const response = new HrmsAllResponseModel(true, 11108, "Employees retrieved successfully", employeeDetailsDTO);
        return response;
      } else {
        throw new ErrorResponse(99998, 'Data not found'); employeeDetailsDTO
      }
    } catch (err) {
      throw err;
    }
  }

  async updateDesignationForDesignationAction(request: EmployeeDesignationRequest): Promise<CommonResponse> {
    try {
      const data = await this.employeeDetailsRepository.findOne({ where: { employeeId: request.employeeId } })
      const update = await this.employeeDetailsRepository.update({ employeeId: request.employeeId }, { designation: request.designation })
      if (update.affected) {
        return new CommonResponse(true, 11111, 'Designation updated in employees details')
      } else {
        return new CommonResponse(false, 1110, 'Error while updating designation in employee details ')
      }
    } catch (err) {
      return err
    }
  }
  async updateDepartmentForDesignationAction(request: EmployeeDepartmentRequest): Promise<CommonResponse> {
    try {
      const data = await this.employeeDetailsRepository.findOne({ where: { employeeId: request.employeeId } })
      const update = await this.employeeDetailsRepository.update({ employeeId: request.employeeId }, { departmentId: request.departmentId })
      if (update.affected) {
        return new CommonResponse(true, 11111, 'Department updated in employees details')
      } else {

        return new CommonResponse(false, 1110, 'Error while updating department in employee details ')
      }
    } catch (err) {
      return err
    }

  }

  async getActiveEmployeeByEmpCode(empCode: string): Promise<EmployeeResponseModel> {
    try {
      const employeeEntities: EmployeeDetails = await this.employeeDetailsRepository.findOne({
        where: { employeeCode: empCode, isActive: true }
      });
      const employeeData: EmployeeDetailsDTO = this.employeeDetailsAdapter.convertEntityToDto(employeeEntities);
      if (employeeData) {
        const response = new EmployeeResponseModel(true, 11101, 'employee retrived Successfully', employeeData);
        return response;
      }
      else {
        throw new ErrorResponse(11106, 'Something went wrong');
      }
    } catch (err) {
      throw err;
    }
  }
  async getEmployeesByUnitId(req: EmployeeByUnitRequest): Promise<AllEmployeesResponseModel> {

    try {
      const result = await this.employeeDetailsRepository
        .createQueryBuilder('employeeDetails')
        .orderBy("employeeDetails.employeeName", "ASC")
        .where(`employeeDetails.unit_id = ` + req.unitId)
        .getMany();
      if (result) {
        return new AllEmployeesResponseModel(true, 1111, "Data retreived sucessfully", result)
      } else {
        return new AllEmployeesResponseModel(false, 1111, "Error occured")
      }
    }
    catch (err) {
      return err;
    }
  }

  async getEmployeeUnitData(): Promise<CommonResponse> {
    const manager = getManager()
    let query = ` SELECT employee_type, IF(unit_id = 1, COUNT(1), 0) AS kakinada,
      IF(unit_id = 2, COUNT(1), 0) AS Unit1,
        IF(unit_id = 3, COUNT(1), 0) AS Unit2  FROM  ${appSettings.masters_db}.employee_details GROUP BY employee_type`;
    // query = query + ` GROUP BY si.po_number  ORDER BY poNumber`;
    const data = await manager.query(query);
    if (data) {
      return new CommonResponse(true, 1111, 'Data retreived sucessfully', data)
    } else {
      return new CommonResponse(false, 1111, 'No data')
    }
  }

  async getAllInactiveEmployees(): Promise<HrmsAllResponseModel> {
    try {
      const employeeDetailsDTO: EmployeeDetailsDTO[] = [];
      //retrieves all companies
      const EmployeesEntities: EmployeeDetails[] = await this.employeeDetailsRepository.find({
        order: { employeeCode: 'ASC' }, where: { isActive: false },
      });
      if (EmployeesEntities) {
        // converts the data fetched from the database which of type companies array to type StateDto array.
        EmployeesEntities.forEach(EmployeeEntity => {
          const convertedEmployeeDto: EmployeeDetailsDTO = this.employeeDetailsAdapter.convertEntityToDto(
            EmployeeEntity
          );
          employeeDetailsDTO.push(convertedEmployeeDto);
        });
        const response = new HrmsAllResponseModel(true, 11108, "Employees retrieved successfully", employeeDetailsDTO);
        return response;
      } else {
        throw new ErrorResponse(99998, 'Data not found'); employeeDetailsDTO
      }
    } catch (err) {
      throw err;
    }
  }

}


